import { tenantTypeCategory, tenantTypeLabel } from '@axioma/shared';
import { Button, Card, Logotype, Spinner } from '@axioma/ui';
import { useMutation } from '@tanstack/react-query';
import { Building2, ChevronRight, LogOut, Scale, Shield } from 'lucide-react';
import { Navigate, useNavigate } from 'react-router';

import { api, extractErrorMessage } from '../lib/api.js';
import { disconnectAllRealtime } from '../lib/realtime.js';
import { useAuthStore } from '../stores/auth.js';

export function SelectTenant(): JSX.Element {
  const { token, tenants, user, setToken, setCurrentTenant, clear } = useAuthStore();
  const navigate = useNavigate();

  const switchMutation = useMutation({
    mutationFn: async (tenantId: string) => {
      const { data } = await api.post<{ token: string }>('/auth/switch-tenant', { tenantId });
      return { token: data.token, tenantId };
    },
    onSuccess: ({ token, tenantId }) => {
      setToken(token);
      setCurrentTenant(tenantId);
      navigate('/', { replace: true });
    },
  });

  const logout = (): void => {
    void api.post('/auth/logout').catch(() => undefined);
    disconnectAllRealtime();
    clear();
    navigate('/login', { replace: true });
  };

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  // Caso /auth/me ainda esteja carregando os tenants
  if (tenants.length === 0) {
    return (
      <div className="ax-hero-bg flex min-h-screen w-screen items-center justify-center px-6">
        <div className="text-center">
          <Spinner size="lg" variant="aurora" />
          <p className="ax-body mt-5 text-ax-text-secondary">Carregando seus workspaces…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ax-hero-bg flex min-h-screen w-screen items-center justify-center px-6 py-10">
      <div className="w-full max-w-2xl">
        <div className="mb-8 text-center">
          <Logotype size="lg" />
          <p className="ax-editorial mt-3 text-base italic text-ax-text-secondary">
            Antes da prova, o axioma.
          </p>
          <h1 className="mt-8 font-display text-3xl tracking-tight text-ax-text-primary">
            Escolha o workspace
          </h1>
          <p className="ax-body mt-2 text-ax-text-secondary">
            Olá, <strong className="font-medium text-ax-text-primary">{user?.name}</strong>. Cada
            workspace tem dados, processos e memória institucional isolados.
          </p>
        </div>

        <div className="space-y-3">
          {tenants.map((t) => {
            const cat = tenantTypeCategory(t.type);
            const isPublic = cat === 'PROCURADORIA' || cat === 'DEFENSORIA' || cat === 'AUTARQUICA';
            const Icon = cat === 'DEFENSORIA' ? Scale : cat === 'AUTARQUICA' ? Shield : Building2;
            return (
              <Card
                key={t.id}
                interactive
                className="group transition-all hover:border-[color:rgb(58_61_70)]"
                onClick={() => switchMutation.mutate(t.id)}
              >
                <div className="flex items-center gap-4 px-5 py-4">
                  <div
                    className={[
                      'flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-ax-md border',
                      isPublic
                        ? 'border-[rgba(229,165,71,0.30)] bg-[rgba(229,165,71,0.10)] text-ax-aurora'
                        : 'border-[rgba(110,91,255,0.30)] bg-[rgba(110,91,255,0.12)] text-ax-plasma',
                    ].join(' ')}
                  >
                    <Icon size={20} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-display text-base text-ax-text-primary">{t.name}</p>
                    <p className="font-mono text-[11px] uppercase tracking-wider text-ax-text-tertiary">
                      {tenantTypeLabel(t.type)} · {t.role}
                    </p>
                  </div>
                  <ChevronRight
                    size={18}
                    className="text-ax-text-tertiary transition-transform group-hover:translate-x-1 group-hover:text-ax-aurora"
                  />
                </div>
              </Card>
            );
          })}
        </div>

        {switchMutation.isError && (
          <p className="mt-4 text-center text-xs text-ax-critical">
            {extractErrorMessage(switchMutation.error)}
          </p>
        )}

        <div className="mt-8 text-center">
          <Button variant="ghost" size="sm" leftIcon={<LogOut size={14} />} onClick={logout}>
            Sair
          </Button>
        </div>
      </div>
    </div>
  );
}
